import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Upload, Loader2, CloudDownload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useDebouncedValue } from '@/lib/utils';
import { useStorageContentImport } from '@/hooks/universal-storage/useStorageContentImport';
import { useContentSelection } from '@/hooks/shared/useContentSelection';
import { ContentItemCard } from '@/components/shared/import/ContentItemCard';
import { ImportEmptyFallback } from '@/components/shared/import/ImportEmptyFallback';
import DashboardModal from '@/components/dashboard-modal';
import { useUserLibraryCategories } from '@/hooks/universal-storage/use-library-categories';
import { useIsMobile } from '@/hooks/layout/use-mobile';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface StorageContentImporterProps {
  contentType: 'images' | 'research' | 'pages';
  isOpen: boolean;
  onClose: () => void;
  onBulkImport: (selectedIds: string[], categoryId?: string) => Promise<void>;
  layout?: 'list' | 'grid';
  showBulkActions?: boolean;
  showClearButton?: boolean;
  showSearch?: boolean;
  className?: string;
}

export const StorageContentImporter: React.FC<StorageContentImporterProps> = ({
  contentType,
  isOpen,
  onClose,
  onBulkImport,
  layout = 'list',
  showBulkActions = true,
  showClearButton = true,
  showSearch = true,
  className
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const debouncedSearch = useDebouncedValue(searchQuery, 300);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  // Get categories for folder assignment
  const { data: categories = [] } = useUserLibraryCategories();

  // Import functionality using the new storage-aware hook
  const {
    data: allItems,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    totalCount
  } = useStorageContentImport({
    contentType,
    searchQuery: debouncedSearch
  });

  // Selection functionality
  const {
    selectedItems,
    toggleItemSelection,
    clearSelection,
    isSelected,
    selectedCount
  } = useContentSelection();

  // Reset selection when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      clearSelection();
      setSearchQuery('');
      setSelectedCategoryId('');
    }
  }, [isOpen, clearSelection]);

  // Intersection observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Clear search
  const handleClearSearch = () => {
    setSearchQuery('');
  };

  // Handle bulk import with optional category assignment
  const handleBulkImport = async () => {
    if (selectedCount === 0) return;
    
    setIsImporting(true);
    try {
      const categoryId = selectedCategoryId || undefined;
      await onBulkImport(Array.from(selectedItems), categoryId);
      clearSelection(); // Clear selections after successful import
      onClose(); // Close modal after successful import
    } catch (error) {
      console.error('Error importing items to storage:', error);
    } finally {
      setIsImporting(false);
    }
  };

  // Get content type display info
  const getContentTypeInfo = () => {
    switch (contentType) {
      case 'pages':
        return {
          title: "Add Documents to Storage",
          buttonColor: "bg-purple-600 hover:bg-purple-700",
          emptyMessage: `No ${contentType === 'pages' ? 'documents' : contentType} available for import`
        };
      case 'research':
        return {
          title: "Add Research to Storage",
          buttonColor: "bg-green-600 hover:bg-green-700",
          emptyMessage: `No ${contentType} available for import`
        };
      case 'images':
      default:
        return {
          title: "Add Images to Storage",
          buttonColor: "bg-blue-600 hover:bg-blue-700",
          emptyMessage: `No ${contentType} available for import`
        };
    }
  };

  const contentTypeInfo = getContentTypeInfo();

  if (!isOpen) return null;

  return (
    <DashboardModal
      isOpen={isOpen}
      onClose={onClose}
      title={contentTypeInfo.title}
      icon={CloudDownload}
      maxWidth="max-w-4xl"
      variant={contentType === 'images' ? 'blue' : contentType === 'research' ? 'green' : 'purple'}
    >
      <div className={cn("flex flex-col -mx-6 -mt-6 -mb-6", className)}>
          {/* Search Bar */}
          {showSearch && (
            <div className="px-6 py-3 border-b border-gray-200/50 dark:border-gray-700/50">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10 pointer-events-none" size={16} />
                <Input
                  placeholder={`Search your ${contentType === 'pages' ? 'documents' : contentType}...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 pr-9 relative z-0"
                />
                {searchQuery && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 z-10"
                    title="Clear search"
                    aria-label="Clear search"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons Row */}
          {showBulkActions && (
            <div className="px-6 py-3 border-b border-gray-200/50 dark:border-gray-700/50">
              <div className={cn(
                "flex gap-3",
                isMobile ? "flex-col" : "flex-row items-center"
              )}>
                {/* Category Selection */}
                <div className={cn(isMobile ? "w-full" : "flex-1")}>
                  <Select value={selectedCategoryId} onValueChange={setSelectedCategoryId}>
                    <SelectTrigger className="h-9 w-full">
                      <SelectValue placeholder="Choose folder (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(category => (
                        <SelectItem key={category.id} value={category.id}>
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Import Button */}
                <Button
                  onClick={handleBulkImport}
                  disabled={selectedCount === 0 || isImporting}
                  className={cn(
                    "text-white flex-shrink-0",
                    isMobile ? "w-full" : "min-w-[140px]",
                    contentTypeInfo.buttonColor
                  )}
                  size="sm"
                >
                  {isImporting ? (
                    <>
                      <Loader2 size={14} className="mr-2 animate-spin" />
                      Adding to Storage...
                    </>
                  ) : (
                    <>
                      <Upload size={14} className="mr-2" />
                      Add to Storage ({selectedCount})
                    </>
                  )}
                </Button>
                
                {/* Clear Selection Button - Hidden on mobile */}
                {showClearButton && !isMobile && (
                  <Button
                    onClick={clearSelection}
                    disabled={selectedCount === 0}
                    variant="outline"
                    size="sm"
                    className="flex-shrink-0"
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Items List/Grid */}
          <div className={cn(
            "flex-1",
            isMobile ? "" : "overflow-y-auto max-h-[70vh]"
          )}>
            {isLoading && allItems.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 size={20} className="animate-spin text-gray-400" />
                <span className="ml-2 text-sm text-gray-500">Loading {contentType}...</span>
              </div>
            ) : allItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="text-center">
                  <Upload className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                    {contentTypeInfo.emptyMessage}
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 mb-4">
                    {searchQuery 
                      ? `No ${contentType} match your search "${searchQuery}"`
                      : `All your ${contentType} are already in Storage or you haven't created any yet.`
                    }
                  </p>
                  <ImportEmptyFallback 
                    contentType={contentType}
                    searchQuery={searchQuery}
                  />
                </div>
              </div>
            ) : (
              <div className={cn(
                "p-2",
                layout === 'grid' && contentType === 'images' 
                  ? "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3"
                  : "grid grid-cols-1 gap-1"
              )}>
                <AnimatePresence>
                  {allItems.map((item: any, index: number) => (
                    <ContentItemCard
                      key={item.id}
                      item={item}
                      contentType={contentType}
                      isSelected={isSelected(item.id)}
                      onToggleSelection={toggleItemSelection}
                      index={index}
                      layout={layout}
                    />
                  ))}
                </AnimatePresence>
                
                {/* Load More Trigger */}
                {hasNextPage && (
                  <div ref={loadMoreRef} className="py-4 flex justify-center">
                    {isFetchingNextPage ? (
                      <Loader2 size={20} className="animate-spin text-gray-400" />
                    ) : (
                      <span className="text-sm text-gray-400">Scroll for more...</span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
    </DashboardModal>
  );
};

export default StorageContentImporter; 